import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import { useTutorial } from "../lib/tutorial";
import { Sun, Moon, LogOut, Dumbbell, Menu, ChevronDown, GraduationCap } from "lucide-react";

const NAV = [
    { to: "/dashboard", label: "Dashboard" },
    { to: "/workouts", label: "Workouts" },
    { to: "/exercises", label: "Exercise Library" },
    { to: "/progress", label: "Progress" },
    { to: "/nutrition", label: "Nutrition" },
    { to: "/education", label: "Education" },
    { to: "/previous", label: "History" },
    { to: "/ranks", label: "Ranks" },
    { to: "/squad", label: "Squad" },
    { to: "/account", label: "Account" },
    { to: "/pricing", label: "Pricing" },
];

export default function Navbar() {
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const { openTutorial } = useTutorial();
    const navigate = useNavigate();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);
    const tabsRef = useRef(null);
    const menuRef = useRef(null);
    const [tabsHaveOverflow, setTabsHaveOverflow] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    // Track whether the horizontal tab strip is actually scrollable. Used to
    // toggle the right-edge fade + chevron hint so we don't show a "more"
    // indicator on a desktop where every tab is already visible.
    useEffect(() => {
        const el = tabsRef.current;
        if (!el) return;
        const update = () => {
            const overflowing = el.scrollWidth - el.clientWidth > 8;
            const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 8;
            setTabsHaveOverflow(overflowing && !atEnd);
        };
        update();
        el.addEventListener("scroll", update, { passive: true });
        window.addEventListener("resize", update);
        return () => {
            el.removeEventListener("scroll", update);
            window.removeEventListener("resize", update);
        };
    }, [user]);

    // Center the active tab inside the scrollable strip whenever the route
    // changes. Browser's native scrollLeft clamping prevents the strip from
    // showing whitespace at the edges, so end-of-list tabs stay flush even
    // when we ask to centre them. Skipped when there's no overflow.
    useEffect(() => {
        const strip = tabsRef.current;
        if (!strip) return;
        const active = strip.querySelector('a[aria-current="page"]');
        if (!active) return;
        // Defer one frame so the DOM is laid out.
        const id = requestAnimationFrame(() => {
            const stripRect = strip.getBoundingClientRect();
            const activeRect = active.getBoundingClientRect();
            const desiredScrollLeft =
                strip.scrollLeft + (activeRect.left - stripRect.left) -
                (stripRect.width / 2 - activeRect.width / 2);
            strip.scrollTo({ left: Math.max(0, desiredScrollLeft), behavior: "smooth" });
        });
        return () => cancelAnimationFrame(id);
    }, [location.pathname, user]);

    // Click-outside / Escape to close the "More" dropdown
    useEffect(() => {
        if (!menuOpen) return;
        const onClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
        document.addEventListener("mousedown", onClick);
        document.addEventListener("touchstart", onClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onClick);
            document.removeEventListener("touchstart", onClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [menuOpen]);

    const linkClass = ({ isActive }) =>
        `relative px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] transition-colors whitespace-nowrap rounded-sm
        ${isActive
            ? "bg-accent-red text-white"
            : "text-foreground/75 hover:text-foreground hover:bg-secondary"}`;

    return (
        <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur" data-testid="main-navbar">
            {/* Top bar: brand + theme + auth */}
            <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
                <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2" data-testid="nav-logo">
                    <Dumbbell className="h-6 w-6 text-accent-red" />
                    <span className="font-display text-2xl font-black uppercase tracking-tight">Hyper<span className="text-accent-red">gains</span></span>
                </Link>
                <div className="flex items-center gap-2">
                    <button onClick={toggle} data-testid="theme-toggle" aria-label="Toggle theme" className="rounded-sm border border-border p-2 hover:bg-secondary">
                        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    </button>
                    {user ? (
                        <>
                            {/* "More" dropdown — duplicates every tab as a vertical menu so users
                                who don't realise the tab strip is horizontally scrollable can
                                still reach Squad / Account / Pricing / Admin. Acts as a tab
                                catalogue + Sign-out in one menu. */}
                            <div ref={menuRef} className="relative">
                                <button
                                    onClick={() => setMenuOpen((o) => !o)}
                                    aria-label="Menu"
                                    aria-expanded={menuOpen}
                                    data-testid="nav-more"
                                    className="flex items-center gap-1.5 rounded-sm border border-border px-3 py-2 text-xs font-bold uppercase tracking-wider hover:bg-secondary"
                                >
                                    <Menu className="h-4 w-4" /> Menu <ChevronDown className={`h-3 w-3 transition-transform ${menuOpen ? "rotate-180" : ""}`} />
                                </button>
                                {menuOpen && (
                                    <div
                                        role="menu"
                                        data-testid="nav-more-popover"
                                        className="absolute right-0 z-50 mt-2 w-56 origin-top-right border border-border bg-card shadow-[0_14px_40px_-4px_rgba(0,0,0,0.6)]"
                                    >
                                        <div className="label-eyebrow border-b border-border px-3 py-2 text-accent-red">All tabs</div>
                                        <div className="max-h-[70vh] overflow-y-auto py-1">
                                            {NAV.map((n) => (
                                                <NavLink
                                                    key={n.to}
                                                    to={n.to}
                                                    onClick={() => setMenuOpen(false)}
                                                    data-testid={`nav-more-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                                                    className={({ isActive }) =>
                                                        `block px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${isActive ? "bg-accent-red/10 text-accent-red" : "text-foreground/85 hover:bg-secondary"}`
                                                    }
                                                >
                                                    {n.label}
                                                </NavLink>
                                            ))}
                                            {user.role === "admin" && (
                                                <NavLink
                                                    to="/admin"
                                                    onClick={() => setMenuOpen(false)}
                                                    data-testid="nav-more-admin"
                                                    className={({ isActive }) =>
                                                        `block px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${isActive ? "bg-accent-red/10 text-accent-red" : "text-foreground/85 hover:bg-secondary"}`
                                                    }
                                                >
                                                    Admin
                                                </NavLink>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => { setMenuOpen(false); openTutorial(); }}
                                            data-testid="nav-more-tutorial"
                                            className="flex w-full items-center gap-2 border-t border-border px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-foreground/85 transition-colors hover:bg-secondary"
                                        >
                                            <GraduationCap className="h-3.5 w-3.5" /> Show tutorial
                                        </button>
                                        <button
                                            onClick={() => { setMenuOpen(false); handleLogout(); }}
                                            data-testid="nav-more-logout"
                                            className="flex w-full items-center gap-2 border-t border-border px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-foreground hover:bg-secondary"
                                        >
                                            <LogOut className="h-3.5 w-3.5" /> Sign out
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <Link to="/login" data-testid="nav-signin" className="rounded-sm bg-accent-red px-4 py-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-[color:var(--accent-hover)]">Sign in</Link>
                    )}
                </div>
            </div>

            {/* Tabs strip — always visible when logged in (horizontal scroll on mobile) */}
            {user && (
                <div className="relative border-t border-border bg-card/40">
                    <nav
                        ref={tabsRef}
                        className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-2 py-2 sm:px-6 scrollbar-thin"
                        data-testid="nav-tabs"
                    >
                        {NAV.map(n => (
                            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`} className={linkClass}>
                                {n.label}
                            </NavLink>
                        ))}
                        {user.role === "admin" && (
                            <NavLink to="/admin" data-testid="nav-admin" className={linkClass}>Admin</NavLink>
                        )}
                    </nav>
                    {tabsHaveOverflow && (
                        <div
                            data-testid="nav-tabs-more-hint"
                            className="pointer-events-none absolute right-0 top-0 flex h-full items-center bg-gradient-to-l from-background via-background/85 to-transparent pl-8 pr-2 sm:pr-3"
                        >
                            <ChevronDown className="h-4 w-4 -rotate-90 text-accent-red animate-pulse" aria-hidden="true" />
                            <span className="ml-1 text-[10px] font-bold uppercase tracking-widest text-accent-red">More</span>
                        </div>
                    )}
                </div>
            )}

            {/* The mobile drawer was removed in favour of the "Menu" dropdown,
                which handles tabs + sign-out across every breakpoint. */}
        </header>
    );
}
